import React, { useEffect, useRef, useState } from "react";
import { ISessionState } from "source/models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as riskService from "source/models/services/riskService";
import { List } from "source/components/framework/List/List";
import { Box } from "@mui/material";
import { RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { getRiskPageGeneralFilterFromFilterState, getShowFilterCondition, IRiskPageFilterLists, IRiskPageFilterState } from "./RiskPageUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { HtmlReportPanel } from "./HtmlReportPanel";
import { RiskPageReportHtmlFilterCondition } from "./RiskPageReportHtmlFilterCondition";
import { IReportItem } from "source/models/report/reportItem";
import { RiskPageReportHtmlContainer } from "./RiskPageReportHtmlContainer";

interface IRiskPageReportHtmlTreatmentsRiskReportProp extends IRiskPageFilterLists, IRiskPageFilterState {
    sessionState: ISessionState;
    companyId: string;
    settings: IRiskSettingEdit;
    report: IReportItem;
    dataId?: string;
    isPrinting: boolean;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    onSetInitialized: (initialized: boolean) => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

type TreatmentsRiskReportLine = {
    id: string;
    riskAssessmentId: string;
    hasAssesment: boolean;
    code: string;
    title: string;
    treatments: string;
    personsInCharge: string;
    treatmentTargetDate: number;
    riskTreatmentState: number;
}

const sortTreatmentsRiskReportLines = (lines: TreatmentsRiskReportLine[], column: string, asc: boolean): TreatmentsRiskReportLine[] => {
    if (!lines || lines.length < 2) return lines;
    if (column === "code") {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * Base.strCompare(a.code, b.code)); });
    } else if (column === "title") {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.title, b.title) + Base.strCompare(a.code, b.code))); });
    } else if (column === "riskTreatmentState") {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.riskTreatmentState, b.riskTreatmentState) + Base.strCompare(a.code, b.code))); });
    } else if (column === "personsInCharge") {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.personsInCharge, b.personsInCharge) + Base.strCompare(a.code, b.code))); });
    } else if (column === "treatments") {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.strCompare(a.treatments, b.treatments) + Base.strCompare(a.code, b.code))); });
    } else {
        return lines.sort((a: TreatmentsRiskReportLine, b: TreatmentsRiskReportLine) => { return (!a || !b) ? 0 : ((asc ? 1 : -1) * (10 * Base.numCompare(a.treatmentTargetDate ?? 0, b.treatmentTargetDate ?? 0) + Base.strCompare(a.code, b.code))); });
    }
};

interface IRiskPageReportHtmlTreatmentsRiskReportState {
    initialized: boolean;
    pageSize: number;
    page: number;
    lines: TreatmentsRiskReportLine[];
    //Visibilty options
    showFilterCondition: boolean;
}

export const RiskPageReportHtmlTreatmentsRiskReport = (props: IRiskPageReportHtmlTreatmentsRiskReportProp) => {
    const _isMounted = useRef(false);
    const loadAssessmentDataId = useRef(0);

    const [state, setState] = useState<IRiskPageReportHtmlTreatmentsRiskReportState>({
        initialized: false,
        pageSize: 10000,
        page: 1,
        lines: [],
        showFilterCondition: false,
    });

    useEffect(() => {
        _isMounted.current = true;

        return () => {
            _isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        props.onSetInitialized(false);
    }, [props.companyId]);

    useEffect(() => {
        if (!props.report?.taskDataRow) return;
        setState(old => ({
            ...old,
            showFilterCondition: getShowFilterCondition(props.report),
        }));
    }, [props.report?.taskDataRow]);

    useEffect(() => {
        const loadAssessmentData = async (assessmentIds: string[]) => {
            const batchSize = 100;
            const batches = [];
            const loadAssessmentDataIdCurrent = loadAssessmentDataId.current;
            for (let i = 0; i < assessmentIds.length; i += batchSize) {
                batches.push(assessmentIds.slice(i, i + batchSize));
            }
            for (const batch of batches) {
                if (!_isMounted.current || loadAssessmentDataId.current !== loadAssessmentDataIdCurrent) break;
                const assessmentsResult = await AppUtils.callService(() => riskService.getRiskAssessmentEditItems(props.companyId, batch), false);
                if (!assessmentsResult) continue;
                if (!_isMounted.current || loadAssessmentDataId.current !== loadAssessmentDataIdCurrent) break;
                setState((old) => {
                    const lines = [...old.lines];
                    const linesDict = Object.fromEntries(lines.filter(line => line.riskAssessmentId).map(line => [line.riskAssessmentId, line]));
                    for (const item of assessmentsResult.items) {
                        item.personsInCharge = ModifyUser.sortModifyUsers(item.personsInCharge, { currentUserCode: props.sessionState.code });
                        if (!linesDict[item.id]) continue;
                        linesDict[item.id].personsInCharge = item.personsInCharge.map(user => user.name).join(", ");
                    }
                    return {
                        ...old,
                        lines
                    };
                });
            }
        };

        const loadData = async () => {
            const generalFilter = getRiskPageGeneralFilterFromFilterState(props);
            const result = await AppUtils.callService(() => riskService.getRiskListItems(props.companyId, state.pageSize, 1,
                false, null, generalFilter.typeIds, generalFilter.unitIds, generalFilter.stateIds, generalFilter.riskCategoryIds, generalFilter.riskProcessIds,
                generalFilter.riskStandardIds, generalFilter.riskStakeholderIds, generalFilter.residualRiskIds, generalFilter.riskTreatmentStateIds,
                generalFilter.handlerUserCodes, generalFilter.owners, props.sortColumn, props.sortOrderIsAsc), false);
            if (!_isMounted.current || !result) return;
            setState((old) => ({
                ...old,
                initialized: true,
                lines: result.items.map(item => ({
                    id: item.id,
                    riskAssessmentId: item.riskAssessmentId,
                    hasAssesment: item.hasAssesment,
                    code: item.code,
                    title: item.title,
                    treatments: item.treatments,
                    personsInCharge: "",
                    treatmentTargetDate: item.treatmentTargetDate,
                    riskTreatmentState: item.riskTreatmentState,
                })),
            }));
            props.onSetInitialized(true);
            loadAssessmentDataId.current++;
            loadAssessmentData(result.items.map(i => i.riskAssessmentId).filter(id => id));
        };

        if (!props.companyId) return;
        loadData();
    }, [props.companyId,
        props.dataId,
        props.selectedRiskTypeIds.join(","),
        props.selectedUnitIds.join(","),
        props.selectedStateIds.join(","),
        props.selectedRiskCategoryIds.join(","),
        props.selectedRiskProcessIds.join(","),
        props.selectedRiskStandardIds.join(","),
        props.selectedRiskStakeholderIds.join(","),
        props.selectedResidualRiskIds.join(","),
        props.selectedRiskTreatmentStateIds.join(","),
        props.selectedHandlerUserCodes.join(","),
        props.selectedOwners.join(",")]);

    const sortList = (column: string, asc: boolean) => {
        const lines = state.lines.slice(0);
        sortTreatmentsRiskReportLines(lines, column, asc);
        props.onSetSortData(column, asc);
        setState(old => ({
            ...old,
            lines
        }));
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, _nextPage: boolean) => {
        sortList(sortColumn, sortOrderIsAsc);
    };

    const getContent = (isPrinting: boolean) => {
        return (<>
            {state.showFilterCondition &&
                <RiskPageReportHtmlFilterCondition
                    isPrinting={isPrinting}
                    settings={props.settings}
                    {...props}
                />
            }
            <Box sx={{ marginTop: state.showFilterCondition ? 2 : 0 }}>
                <HtmlReportPanel
                    title={Translations.RiskRegisterSummary}
                    height={isPrinting ? "auto" : undefined}
                    isPrinting={isPrinting}
                >
                    <List
                        intialized={true}
                        isLoading={false}
                        hasMore={false}
                        height="auto"
                        header={{
                            columns: [
                                { size: 1, column: "code", content: Translations.Code },
                                { size: 2, column: "title", content: Translations.Title },
                                { size: 4, column: "treatments", content: Translations.RiskTreatments },
                                { size: 2, column: "personsInCharge", content: Translations.PersonInCharge },
                                { size: 2, column: "treatmentTargetDate", content: Translations.TreatmentTargetDate },
                                { size: 1, column: "riskTreatmentState", content: Translations.State }
                            ]
                        }}
                        lines={
                            state.lines.map((item) => {
                                return {
                                    columns: [
                                        { size: 1, content: item.code },
                                        { size: 2, content: <Box>{item.title}</Box> },
                                        { size: 4, content: <Box>{item.treatments}</Box> },
                                        { size: 2, content: item.hasAssesment ? <Box>{item.personsInCharge}</Box> : null },
                                        { size: 2, content: item.hasAssesment ? Base.timeToDateStr(item.treatmentTargetDate) : null },
                                        { size: 1, content: item.hasAssesment ? <Box>{RiskListItem.getRiskTreatmentStateText(item.riskTreatmentState)}</Box> : null },
                                    ],
                                    id: item.id
                                };
                            })
                        }
                        sortColumn={props.sortColumn}
                        sortOrderIsAsc={props.sortOrderIsAsc}
                        selectedId={""}
                        checkedIds={[]}
                        onSetSelectedIds={() => {}}
                        onSearchLines={handleSearchLines}
                    />
                </HtmlReportPanel>
            </Box>
        </>);
    };

    if (!state.initialized) return null;

    return (
        <RiskPageReportHtmlContainer
            isPrinting={props.isPrinting}
            getContent={getContent}
        />
    );
};